import { createSlice } from '@reduxjs/toolkit';
import { Duel } from 'src/types/data-types';

import { getMainData, getDuels, getStatements, sendResults } from './actions';
import { STALE_QUIZ_REQUEST, VERSION_MISMATCH } from './actions/match-quiz-version';

type DataState = {
  main: any;
  duels: Record<string, Duel> | null;
  statements: any;
  results: any;
  loadingMain: boolean;
  loadingDuels: boolean;
  loadingStatements: boolean;
  loadingResults: boolean;
  versionMismatch: boolean;
  error: string | null;
};

function applyVersionMismatch(state: DataState) {
  state.versionMismatch = true;
  state.duels = null;
  state.statements = null;
  state.loadingDuels = false;
  state.loadingStatements = false;
  state.error = null;
  state.main = {
    ...state.main,
    versionId: null,
    parties: [],
  };
}

const initialState: DataState = {
  main: {},
  duels: null,
  statements: null,
  results: [],
  loadingMain: false,
  loadingDuels: false,
  loadingStatements: false,
  loadingResults: false,
  versionMismatch: false,
  error: null,
};

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    clearResults: state => {
      state.results = [];
      state.loadingResults = false;
      state.error = null;
    },
    markVersionMismatch: state => {
      applyVersionMismatch(state);
    },
    clearVersionMismatch: state => {
      state.versionMismatch = false;
    },
    restoreResults: (state, action: { payload: unknown }) => {
      state.results = action.payload;
      state.loadingResults = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // Main content and Parties
      .addCase(getMainData.pending, state => {
        state.loadingMain = true;
        state.error = null;
      })
      .addCase(getMainData.fulfilled, (state, action) => {
        state.loadingMain = false;
        state.main = action.payload;
      })
      .addCase(getMainData.rejected, (state, action) => {
        state.loadingMain = false;
        state.error = action.error.message ?? 'Failed to load parties';
      })

      // Duels
      .addCase(getDuels.pending, state => {
        state.loadingDuels = true;
        state.error = null;
      })
      .addCase(getDuels.fulfilled, (state, action) => {
        state.loadingDuels = false;

        if (state.versionMismatch) {
          state.duels = null;
          return;
        }

        state.duels = action.payload.duels ?? {};
      })
      .addCase(getDuels.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.loadingDuels = false;

        if (action.payload === STALE_QUIZ_REQUEST) {
          return;
        }

        if (action.payload === VERSION_MISMATCH) {
          applyVersionMismatch(state);
          return;
        }

        state.error = action.error.message ?? 'Failed to load Duels';
      })

      // Statements
      .addCase(getStatements.pending, state => {
        state.loadingStatements = true;
        state.error = null;
      })
      .addCase(getStatements.fulfilled, (state, action) => {
        state.loadingStatements = false;

        if (state.versionMismatch) {
          state.statements = null;
          return;
        }

        state.statements = action.payload.statements ?? [];
      })
      .addCase(getStatements.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.loadingStatements = false;

        if (action.payload === STALE_QUIZ_REQUEST) {
          return;
        }

        if (action.payload === VERSION_MISMATCH) {
          applyVersionMismatch(state);
          return;
        }

        state.error = action.error.message ?? 'Failed to load Statements';
      })

      // Calculate results
      .addCase(sendResults.pending, (state) => {
        state.loadingResults = true;
        state.error = null;
      })
      .addCase(sendResults.fulfilled, (state, action) => {
        state.loadingResults = false;
        state.results = action.payload;
      })
      .addCase(sendResults.rejected, (state, action) => {
        state.loadingResults = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearResults, restoreResults, markVersionMismatch, clearVersionMismatch } = dataSlice.actions;
export default dataSlice.reducer;